import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';

import { useFeatureGate } from '@ctct/awesome-feature-flags';
import { trackAction } from '@ctct/distui-helpers';
import { IconButton, Button, Alert, HeaderBar, HeaderBarSection } from '@ctct/ui-react';

import AddUser from './components/AddUser';
import UserTable from './components/UserTable';
import UserUpsell from './components/UserUpsell';
import doGetUsers, { accountRoles } from './utils';
import AccountSvc from '../../api/AccountSvc';
import {
  getAccount,
  getAccountData,
  getAccountRole,
  getParentPartnerSummary,
  getPartnerOwnerSummary,
} from '../../api/DistUI';
import ProductSvc from '../../api/ProductSvc';
import { checkSsoInviteEnabled } from '../../utils/ssoInviteUtils';
import FeatureUpgradeDialog from '../FeatureUpgradeDialog';
import './ManageUsers.scss';

const propTypes = {
  t: PropTypes.func.isRequired,
  autoAddNew: PropTypes.bool,
};
const defaultProps = {
  autoAddNew: false,
};

const ManageUsers = ({ t, autoAddNew }) => {
  const ssoInviteGate = useFeatureGate({
    name: 'sso_invite_new_users',
    defaultValue: false,
  });

  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [customerId, setCustomerId] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [remainingUsers, setRemainingUsers] = useState(0);
  const [showAddUser, setShowAddUser] = useState(autoAddNew);
  const [alertData, setAlertData] = useState(null);
  const [allowedUpgrade, setAllowedUpgrade] = useState(false);
  const [pro, setPro] = useState(false);
  const [isLoggedInUserSiteOwner, setIsLoggedInUserSiteOwner] = useState(false);
  const [isFeatureUpgradeDialogOpen, setIsFeatureUpgradeDialogOpen] = useState(false);
  const [paymentSuccessful, setPaymentSuccessful] = useState(null);
  const [hasSsoFeature, setHasSsoFeature] = useState(false);
  const [hasSsoConfigured, setHasSsoConfigured] = useState(false);
  const [partnerId, setPartnerId] = useState(null);
  const [distuiAccount, setDistuiAccount] = useState(null);

  const getUsers = async () => {
    try {
      const localUsers = await doGetUsers();

      setUsers(localUsers);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('exception getting users', e);
    }
  };

  const getInvitations = async () => {
    try {
      const invitationsRsp = await AccountSvc.getInvitations();

      setInvitations(invitationsRsp.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('exception getting invitations', e);
    }
  };

  const onAddUserClose = async () => {
    getInvitations();
    setShowAddUser(false);
  };

  const getPlan = (userCount, planDetail) => {
    const actions = userCount?.data?.actions;

    setAllowedUpgrade(actions != null && actions[0] === 'UPGRADE');
    setTotalUsers(userCount?.data?.allowance?.total || 0);
    setRemainingUsers(userCount?.data?.allowance?.remaining || 0);
    setPro(planDetail && planDetail.currentPlan.id === 193);
  };

  useEffect(() => {
    (async () => {
      // call all endpoints in parallel
      const [
        { data: invitationsRsp },
        localUsers,
        userCount,
        { planDetail },
        accountRsp,
        role,
        partnerOwnerSummary,
        parentPartnerSummary,
        distuiAccount,
      ] = await Promise.all([
        AccountSvc.getInvitations(),
        doGetUsers(),
        ProductSvc.getUserCount(),
        getAccountData(),
        AccountSvc.getAccount(),
        getAccountRole(),
        getPartnerOwnerSummary(),
        getParentPartnerSummary(),
        getAccount(),
      ]);

      // use values to initialize here
      setInvitations(invitationsRsp);
      setUsers(localUsers);
      getPlan(userCount, planDetail);
      setCustomerId(accountRsp?.data?.customerId);
      setFirstName(accountRsp?.data?.firstName);
      setLastName(accountRsp?.data?.lastName);
      setIsLoggedInUserSiteOwner(role === accountRoles.SITE_OWNER || role === accountRoles.PARTNER_IMPERSONATION);
      setPartnerId(partnerOwnerSummary?.partnerUID ?? parentPartnerSummary?.partnerUID);
      setDistuiAccount(distuiAccount);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (partnerId !== null && partnerId !== undefined && !ssoInviteGate.isLoading) {
        const statsigGateEnabled = ssoInviteGate?.data ?? false;
        const { hasSsoFeature: partnerHasSsoFeature, hasSsoConfigured: partnerHasSsoConfigured } =
          await checkSsoInviteEnabled(partnerId, statsigGateEnabled);

        setHasSsoFeature(partnerHasSsoFeature);
        setHasSsoConfigured(partnerHasSsoConfigured);
      }
    })();
  }, [partnerId, ssoInviteGate.data, ssoInviteGate.isLoading]);

  const onRefreshUsers = async () => {
    const [userCount, { planDetail }] = await Promise.all([ProductSvc.getUserCount(), getAccountData()]);

    await getUsers();
    getPlan(userCount, planDetail);
  };

  let actualRemainingUsers = remainingUsers;

  if (totalUsers === remainingUsers && totalUsers !== 0) {
    // For a newly provisioned user there is nothing that sets this count to 1 in backend
    // but the account owner already exists so count that user
    actualRemainingUsers = totalUsers - users.length;
  }

  const [featureUpgradeDialogMounted, setFeatureUpgradeDialogMounted] = useState(false);
  const mountUpgradeDialog = () => {
    setFeatureUpgradeDialogMounted(true);
  };

  const onAddNewUser = async () => {
    if (actualRemainingUsers === 0) {
      trackAction({ action: 'Upgrade to add user' });
      setIsFeatureUpgradeDialogOpen(true);
      mountUpgradeDialog();
    } else {
      trackAction({ action: 'Add user' });
      setShowAddUser(true);
    }
  };

  // Wait until we close the feature upgrade dialog to actually reload after a successful payment
  const setFeatureUpgradeDialog = (isOpen) => {
    setIsFeatureUpgradeDialogOpen(isOpen);

    if (isOpen) {
      mountUpgradeDialog();
    }

    if (paymentSuccessful) {
      window.location.reload();
    }
  };

  const alertCloseButton = (
    <IconButton
      title={t('users.close_alert')}
      data-qe-id="users-close-alert"
      glyph="close"
      onClick={() => {
        setAlertData(null);
      }}
    />
  );

  const showAddUserActionsForOwner = isLoggedInUserSiteOwner && (!hasSsoFeature || hasSsoConfigured);

  return (
    <>
      {alertData != null && (
        <Alert
          kind={alertData.kind || 'success'}
          message={alertData.message}
          data-qe-id="alert"
          actions={
            alertData.undoAction ? (
              <>
                <Button
                  text={t(alertData.actionButtonText || 'users.undo_action')}
                  onClick={() => {
                    alertData.undoAction();
                  }}
                />
                {alertCloseButton}
              </>
            ) : (
              alertCloseButton
            )
          }
        />
      )}
      <HeaderBar>
        <HeaderBarSection section="title">
          <h1 data-qe-id="users-header">{t('users.heading', { firstName, lastName })}</h1>
        </HeaderBarSection>
      </HeaderBar>

      <HeaderBar>
        {!pro && (
          <HeaderBarSection section="title">
            <h3>{t(isLoggedInUserSiteOwner ? 'users.subheading' : 'users.non_manage_users')}</h3>
            {totalUsers > 0 && (
              <p>
                <span data-qe-id="users-count-text">
                  {t('users.remainingspots', { totalUsers, remainingUsers: actualRemainingUsers })}
                </span>
                {isLoggedInUserSiteOwner && (
                  <a
                    href="https://knowledgebase.constantcontact.com/articles/KnowledgeBase/41588-Add-and-manage-users-in-your-Constant-Contact-account?lang=en_US"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {t('users.usermanagement_link')}
                  </a>
                )}
              </p>
            )}
          </HeaderBarSection>
        )}
        {showAddUserActionsForOwner && (
          <HeaderBarSection section="actions">
            <Button
              text={actualRemainingUsers === 0 ? t('users.upgrade_button') : t('users.add_button')}
              kind="primary"
              onClick={onAddNewUser}
              onMouseEnter={mountUpgradeDialog}
              data-qe-id="users-add-button"
            />
          </HeaderBarSection>
        )}
      </HeaderBar>
      <UserTable
        t={t}
        customerId={customerId}
        invitations={invitations}
        users={users}
        onRefreshUsers={onRefreshUsers}
        onRefreshInvites={getInvitations}
        setAlertData={setAlertData}
        allowedUpgrade={allowedUpgrade}
        isPro={pro}
        isLoggedInUserSiteOwner={isLoggedInUserSiteOwner}
      />
      <br />
      {actualRemainingUsers === 0 && allowedUpgrade && (
        <UserUpsell
          t={t}
          actions={
            <Button
              text={t('users.upgrade_button')}
              kind="primary"
              onClick={onAddNewUser}
              onMouseEnter={mountUpgradeDialog}
              data-qe-id="users-add-button"
            />
          }
        />
      )}
      {showAddUser && (
        <AddUser
          isOpen
          isSsoAccount={hasSsoConfigured}
          onClose={onAddUserClose}
          onRefreshUsers={onRefreshUsers}
          onOpenUpgradeDialog={() => setFeatureUpgradeDialog(true)}
          t={t}
        />
      )}
      {totalUsers !== 0 && distuiAccount != null && featureUpgradeDialogMounted && (
        <FeatureUpgradeDialog
          isOpen={isFeatureUpgradeDialogOpen}
          setIsOpen={setFeatureUpgradeDialog}
          feature="USER_MGMT"
          activity="CREATE"
          usage={totalUsers + 1}
          paymentSuccessCallback={(success) => setPaymentSuccessful(success)}
        />
      )}
    </>
  );
};

ManageUsers.propTypes = propTypes;
ManageUsers.defaultProps = defaultProps;

export default ManageUsers;
